import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { aiAgentsApi } from './aiAgents.api';
import type { AiAgentsTab } from './aiAgents.types';

export const aiAgentsQueryKeys = {
  all: ['ai-agents'] as const,
  list: (search?: string) => [...aiAgentsQueryKeys.all, 'list', search ?? ''] as const,
  detail: (id: string) => [...aiAgentsQueryKeys.all, 'detail', id] as const,
  recommendations: (agentId?: string) =>
    [...aiAgentsQueryKeys.all, 'recommendations', agentId ?? 'all'] as const,
  tasks: (agentId: string) => [...aiAgentsQueryKeys.all, 'tasks', agentId] as const,
  runs: (agentId: string) => [...aiAgentsQueryKeys.all, 'runs', agentId] as const,
  logs: (agentId: string, runId?: string) =>
    [...aiAgentsQueryKeys.all, 'logs', agentId, runId ?? 'all'] as const,
};

export function useAiAgentsList(search?: string) {
  return useQuery({
    queryKey: aiAgentsQueryKeys.list(search),
    queryFn: async () => {
      const result = await aiAgentsApi.listAgents({ page: 1, pageSize: 50, search });
      if (!result.ok || !result.data) throw new Error(result.error ?? 'Unable to load agents');
      return result.data;
    },
  });
}

export function useAiAgentDetail(agentId: string | null) {
  return useQuery({
    queryKey: aiAgentsQueryKeys.detail(agentId ?? 'none'),
    enabled: Boolean(agentId),
    queryFn: async () => {
      const result = await aiAgentsApi.getAgent(agentId!);
      if (!result.ok || !result.data) throw new Error(result.error ?? 'Unable to load agent');
      return result.data;
    },
  });
}

export function useAiRecommendations(agentId?: string) {
  return useQuery({
    queryKey: aiAgentsQueryKeys.recommendations(agentId),
    queryFn: async () => {
      const result = await aiAgentsApi.listRecommendations({
        page: 1,
        pageSize: 50,
        agentId,
      });
      if (!result.ok || !result.data) {
        throw new Error(result.error ?? 'Unable to load recommendations');
      }
      return result.data;
    },
  });
}

export function useAiAgentTasks(agentId: string | null) {
  return useQuery({
    queryKey: aiAgentsQueryKeys.tasks(agentId ?? 'none'),
    enabled: Boolean(agentId),
    queryFn: async () => {
      const result = await aiAgentsApi.listTasks(agentId!, { page: 1, pageSize: 50 });
      if (!result.ok || !result.data) throw new Error(result.error ?? 'Unable to load tasks');
      return result.data;
    },
  });
}

export function useAiAgentRuns(agentId: string | null) {
  return useQuery({
    queryKey: aiAgentsQueryKeys.runs(agentId ?? 'none'),
    enabled: Boolean(agentId),
    queryFn: async () => {
      const result = await aiAgentsApi.listRuns(agentId!, { page: 1, pageSize: 50 });
      if (!result.ok || !result.data) throw new Error(result.error ?? 'Unable to load runs');
      return result.data;
    },
  });
}

export function useAiAgentLogs(agentId: string | null, runId?: string) {
  return useQuery({
    queryKey: aiAgentsQueryKeys.logs(agentId ?? 'none', runId),
    enabled: Boolean(agentId),
    queryFn: async () => {
      const result = await aiAgentsApi.listLogs(agentId!, { page: 1, pageSize: 100, runId });
      if (!result.ok || !result.data) throw new Error(result.error ?? 'Unable to load logs');
      return result.data;
    },
  });
}

export function useAiAgentsMutations(selectedAgentId: string | null) {
  const queryClient = useQueryClient();

  const invalidateAll = async () => {
    await queryClient.invalidateQueries({ queryKey: aiAgentsQueryKeys.all });
  };

  const updateAgent = useMutation({
    mutationFn: async (input: { status?: 'ACTIVE' | 'PAUSED'; autonomyLevel?: number }) => {
      if (!selectedAgentId) throw new Error('Select an agent first');
      const result = await aiAgentsApi.updateAgent(selectedAgentId, input);
      if (!result.ok || !result.data) throw new Error(result.error ?? 'Unable to update agent');
      return result.data;
    },
    onSuccess: invalidateAll,
  });

  const triggerRun = useMutation({
    mutationFn: async () => {
      if (!selectedAgentId) throw new Error('Select an agent first');
      const result = await aiAgentsApi.triggerRun(selectedAgentId, { trigger: 'MANUAL' });
      if (!result.ok || !result.data) throw new Error(result.error ?? 'Unable to start agent run');
      return result.data;
    },
    onSuccess: invalidateAll,
  });

  const createTask = useMutation({
    mutationFn: async (title: string) => {
      if (!selectedAgentId) throw new Error('Select an agent first');
      const result = await aiAgentsApi.createTask(selectedAgentId, { title });
      if (!result.ok || !result.data) throw new Error(result.error ?? 'Unable to queue task');
      return result.data;
    },
    onSuccess: invalidateAll,
  });

  const recommendationAction = useMutation({
    mutationFn: async (input: { id: string; action: 'APPROVE' | 'REJECT' | 'DISMISS'; reason?: string }) => {
      const result = await aiAgentsApi.recommendationAction(input.id, {
        action: input.action,
        reason: input.reason,
      });
      if (!result.ok || !result.data) {
        throw new Error(result.error ?? 'Unable to update recommendation');
      }
      return result.data.recommendation;
    },
    onSuccess: invalidateAll,
  });

  return { updateAgent, triggerRun, createTask, recommendationAction };
}

export function parseAiAgentsTab(value: string | null): AiAgentsTab {
  if (value === 'recommendations' || value === 'tasks' || value === 'runs' || value === 'logs') {
    return value;
  }
  return 'registry';
}

export function useExecutiveAdvisorRecommendations(limit = 8) {
  const agentsQuery = useAiAgentsList();
  const executiveAgentId = agentsQuery.data?.items?.find(
    (agent) => agent.key === 'executive_advisor_agent',
  )?.id;

  return useQuery({
    queryKey: [...aiAgentsQueryKeys.recommendations(executiveAgentId), 'executive', limit] as const,
    enabled: Boolean(executiveAgentId),
    queryFn: async () => {
      const result = await aiAgentsApi.listRecommendations({
        page: 1,
        pageSize: limit,
        agentId: executiveAgentId,
      });
      if (!result.ok || !result.data) {
        throw new Error(result.error ?? 'Unable to load executive recommendations');
      }
      return result.data;
    },
  });
}

export function useFinanceAgentRecommendations(limit = 8) {
  const agentsQuery = useAiAgentsList();
  const financeAgentId = agentsQuery.data?.items?.find(
    (agent) => agent.key === 'cfo_finance_agent',
  )?.id;

  return useQuery({
    queryKey: [...aiAgentsQueryKeys.recommendations(financeAgentId), 'finance', limit] as const,
    enabled: Boolean(financeAgentId),
    queryFn: async () => {
      const result = await aiAgentsApi.listRecommendations({
        page: 1,
        pageSize: limit,
        agentId: financeAgentId,
      });
      if (!result.ok || !result.data) {
        throw new Error(result.error ?? 'Unable to load finance recommendations');
      }
      return result.data;
    },
  });
}

export function useSchedulingAgentRecommendations(limit = 8) {
  const agentsQuery = useAiAgentsList();
  const schedulingAgentId = agentsQuery.data?.items?.find(
    (agent) => agent.key === 'scheduling_agent',
  )?.id;

  return useQuery({
    queryKey: [...aiAgentsQueryKeys.recommendations(schedulingAgentId), 'scheduling', limit] as const,
    enabled: Boolean(schedulingAgentId),
    queryFn: async () => {
      const result = await aiAgentsApi.listRecommendations({
        page: 1,
        pageSize: limit,
        agentId: schedulingAgentId,
      });
      if (!result.ok || !result.data) {
        throw new Error(result.error ?? 'Unable to load scheduling recommendations');
      }
      return result.data;
    },
  });
}

export function useProcurementAgentRecommendations(limit = 8) {
  const agentsQuery = useAiAgentsList();
  const procurementAgentId = agentsQuery.data?.items?.find(
    (agent) => agent.key === 'procurement_agent',
  )?.id;

  return useQuery({
    queryKey: [...aiAgentsQueryKeys.recommendations(procurementAgentId), 'procurement', limit] as const,
    enabled: Boolean(procurementAgentId),
    queryFn: async () => {
      const result = await aiAgentsApi.listRecommendations({
        page: 1,
        pageSize: limit,
        agentId: procurementAgentId,
      });
      if (!result.ok || !result.data) {
        throw new Error(result.error ?? 'Unable to load procurement recommendations');
      }
      return result.data;
    },
  });
}

export function useSafetyAgentRecommendations(limit = 8) {
  const agentsQuery = useAiAgentsList();
  const safetyAgentId = agentsQuery.data?.items?.find(
    (agent) => agent.key === 'safety_agent',
  )?.id;

  return useQuery({
    queryKey: [...aiAgentsQueryKeys.recommendations(safetyAgentId), 'safety', limit] as const,
    enabled: Boolean(safetyAgentId),
    queryFn: async () => {
      const result = await aiAgentsApi.listRecommendations({
        page: 1,
        pageSize: limit,
        agentId: safetyAgentId,
      });
      if (!result.ok || !result.data) {
        throw new Error(result.error ?? 'Unable to load safety recommendations');
      }
      return result.data;
    },
  });
}

export function useComplianceAgentRecommendations(limit = 8) {
  const agentsQuery = useAiAgentsList();
  const complianceAgentId = agentsQuery.data?.items?.find(
    (agent) => agent.key === 'compliance_agent',
  )?.id;

  return useQuery({
    queryKey: [...aiAgentsQueryKeys.recommendations(complianceAgentId), 'compliance', limit] as const,
    enabled: Boolean(complianceAgentId),
    queryFn: async () => {
      const result = await aiAgentsApi.listRecommendations({
        page: 1,
        pageSize: limit,
        agentId: complianceAgentId,
      });
      if (!result.ok || !result.data) {
        throw new Error(result.error ?? 'Unable to load compliance recommendations');
      }
      return result.data;
    },
  });
}

export function useEstimatingAgentRecommendations(limit = 8) {
  const agentsQuery = useAiAgentsList();
  const estimatingAgentId = agentsQuery.data?.items?.find(
    (agent) => agent.key === 'estimating_agent',
  )?.id;

  return useQuery({
    queryKey: [...aiAgentsQueryKeys.recommendations(estimatingAgentId), 'estimating', limit] as const,
    enabled: Boolean(estimatingAgentId),
    queryFn: async () => {
      const result = await aiAgentsApi.listRecommendations({
        page: 1,
        pageSize: limit,
        agentId: estimatingAgentId,
      });
      if (!result.ok || !result.data) {
        throw new Error(result.error ?? 'Unable to load estimating recommendations');
      }
      return result.data;
    },
  });
}

export function useQaQcAgentRecommendations(limit = 8) {
  const agentsQuery = useAiAgentsList();
  const qaQcAgentId = agentsQuery.data?.items?.find(
    (agent) => agent.key === 'qa_qc_agent',
  )?.id;

  return useQuery({
    queryKey: [...aiAgentsQueryKeys.recommendations(qaQcAgentId), 'qa-qc', limit] as const,
    enabled: Boolean(qaQcAgentId),
    queryFn: async () => {
      const result = await aiAgentsApi.listRecommendations({
        page: 1,
        pageSize: limit,
        agentId: qaQcAgentId,
      });
      if (!result.ok || !result.data) {
        throw new Error(result.error ?? 'Unable to load QA/QC recommendations');
      }
      return result.data;
    },
  });
}

export function useHrWorkforceAgentRecommendations(limit = 8) {
  const agentsQuery = useAiAgentsList();
  const workforceAgentId = agentsQuery.data?.items?.find(
    (agent) => agent.key === 'hr_workforce_agent',
  )?.id;

  return useQuery({
    queryKey: [...aiAgentsQueryKeys.recommendations(workforceAgentId), 'hr-workforce', limit] as const,
    enabled: Boolean(workforceAgentId),
    queryFn: async () => {
      const result = await aiAgentsApi.listRecommendations({
        page: 1,
        pageSize: limit,
        agentId: workforceAgentId,
      });
      if (!result.ok || !result.data) {
        throw new Error(result.error ?? 'Unable to load workforce recommendations');
      }
      return result.data;
    },
  });
}

export function useEquipmentAgentRecommendations(limit = 8) {
  const agentsQuery = useAiAgentsList();
  const equipmentAgentId = agentsQuery.data?.items?.find(
    (agent) => agent.key === 'equipment_agent',
  )?.id;

  return useQuery({
    queryKey: [...aiAgentsQueryKeys.recommendations(equipmentAgentId), 'equipment', limit] as const,
    enabled: Boolean(equipmentAgentId),
    queryFn: async () => {
      const result = await aiAgentsApi.listRecommendations({
        page: 1,
        pageSize: limit,
        agentId: equipmentAgentId,
      });
      if (!result.ok || !result.data) {
        throw new Error(result.error ?? 'Unable to load equipment recommendations');
      }
      return result.data;
    },
  });
}

export function useMarketIntelligenceAgentRecommendations(limit = 8) {
  const agentsQuery = useAiAgentsList();
  const marketAgentId = agentsQuery.data?.items?.find(
    (agent) => agent.key === 'market_intelligence_agent',
  )?.id;

  return useQuery({
    queryKey: [...aiAgentsQueryKeys.recommendations(marketAgentId), 'market-intelligence', limit] as const,
    enabled: Boolean(marketAgentId),
    queryFn: async () => {
      const result = await aiAgentsApi.listRecommendations({
        page: 1,
        pageSize: limit,
        agentId: marketAgentId,
      });
      if (!result.ok || !result.data) {
        throw new Error(result.error ?? 'Unable to load market recommendations');
      }
      return result.data;
    },
  });
}

export function useGrowthMaAgentRecommendations(limit = 8) {
  const agentsQuery = useAiAgentsList();
  const growthAgentId = agentsQuery.data?.items?.find(
    (agent) => agent.key === 'growth___m_a_agent',
  )?.id;

  return useQuery({
    queryKey: [...aiAgentsQueryKeys.recommendations(growthAgentId), 'growth-ma', limit] as const,
    enabled: Boolean(growthAgentId),
    queryFn: async () => {
      const result = await aiAgentsApi.listRecommendations({
        page: 1,
        pageSize: limit,
        agentId: growthAgentId,
      });
      if (!result.ok || !result.data) {
        throw new Error(result.error ?? 'Unable to load growth recommendations');
      }
      return result.data;
    },
  });
}
